import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { RenderMarkdownArticle } from '../lib/markdown';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (_e) {
    return '';
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios.get(`${API}/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  // Set <title> + meta description for crawlers once data lands.
  // <Seo> handles canonical, Open Graph and Twitter cards too.
  // The state hooks above keep the <Seo> render path stable across re-fetches.

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center" data-testid="blog-post-not-found">
            <h1 className="text-3xl font-bold mb-3 text-[#020617]">Post not found</h1>
            <p className="text-[#64748B] mb-6">It might be unpublished or the URL is mistyped.</p>
            <Link to="/blog" className="text-[#0052FF] hover:underline" data-testid="blog-back-list">← Back to the journal</Link>
          </div>
        </div>
        <Footer variant="full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-[#64748B] text-sm">Loading…</div>
        </div>
        <Footer variant="full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#020617] flex flex-col" data-testid="blog-post-page">
      <Seo
        // Drop the " — Forge Ledger" suffix on blog titles — the og:site_name
        // in Seo.jsx already includes "Forge Ledger", so appending the brand
        // again pushed multiple long titles over the 60-char SERP truncation
        // limit (SEO audit flagged 4 posts). The post title alone is much
        // more click-attractive in search results.
        title={post.title}
        description={post.meta_description || post.excerpt}
        path={`/blog/${slug}`}
        image={post.cover_image_url || undefined}
        type="article"
      />
      <div className="flex-1">
      <header className="max-w-3xl mx-auto px-6 sm:px-8 pt-16 pb-8">
        <Link to="/blog" className="text-sm text-[#0052FF] hover:underline" data-testid="blog-back-list">← The Forge journal</Link>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.15]">{post.title}</h1>
        <div className="flex items-center gap-3 text-xs text-[#64748B] mt-4">
          <span>By {post.author}</span>
          <span>·</span>
          <span>{formatDate(post.published_at)}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {post.reading_time_minutes} min read
          </span>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 sm:px-8 pb-24">
        {post.cover_image_url && (
          <img
            src={post.cover_image_url}
            alt={post.title}
            className="w-full rounded-xl mb-10 border border-[#E2E8F0]"
            data-testid="blog-post-cover"
          />
        )}
        <RenderMarkdownArticle text={post.body} className="prose prose-slate max-w-none" />

        <hr className="my-12 border-[#E2E8F0]" />

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="blog-post-cta">
          <h3 className="font-bold text-lg mb-2">Want this maths done for you?</h3>
          <p className="text-sm text-[#475569] mb-4">
            Forge Ledger forecasts your real balance weeks ahead, catches shortfalls early, and reconciles against your actual statement. Free Ember tier — no card needed.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-[#020617] text-white font-medium px-4 py-2 rounded-lg hover:bg-[#0f172a] transition-colors"
            data-testid="blog-post-cta-pricing"
          >
            See pricing
          </Link>
        </div>
      </article>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default BlogPost;
